/** @jsxImportSource theme-ui */

import { useAppSelector } from "app/hooks"
import { SidebarLayout } from "components/SidebarLayout"
import { AerodromeLayer } from "components/atciss/map/AerodromeLayer"
import { AirwayLayer } from "components/atciss/map/AirwayLayer"
import { AreaLayer } from "components/atciss/map/AreaLayer"
import { BackgroundTiles } from "components/atciss/map/BackgroundTiles"
import {
  LateralBorderAgreementLayer,
  VerticalBorderAgreementLayer,
} from "components/atciss/map/BorderAgreementLayer"
import { MapControls } from "components/atciss/map/MapControls"
import { NavaidLayer } from "components/atciss/map/NavaidLayer"
import { SectorLayer } from "components/atciss/map/SectorLayer"
import { SigmetLayer } from "components/atciss/map/SigmetLayer"
import { TooltipDragFix } from "components/atciss/map/TooltipDragFix"
import { WindLayer } from "components/atciss/map/WindLayer"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import { RefObject, useRef } from "react"
import { MapContainer, Pane } from "react-leaflet"
import { selectMapCentre } from "services/configSlice"
import {
  selectAirwayOnMap,
  selectAreasOnMap,
  selectLoaOnMap,
  selectSectorsOnMap,
  selectSigmetOnMap,
} from "services/mapSlice"
import { Flex, ThemeUIStyleObject } from "theme-ui"

export const Map = ({ sx }: { sx?: ThemeUIStyleObject }) => {
  const sectorsOnMap = useAppSelector(selectSectorsOnMap)
  const areasOnMap = useAppSelector(selectAreasOnMap)
  const airwaysOnMap = useAppSelector(selectAirwayOnMap)
  const sigmetOnMap = useAppSelector(selectSigmetOnMap)
  const loaOnMap = useAppSelector(selectLoaOnMap)
  const centre = useAppSelector(selectMapCentre)
  // FIXME react-leaflet type for MapControls seems to be broken
  const mapRef = useRef<L.Map>(null) as RefObject<L.Map>

  return (
    <SidebarLayout sx={sx}>
      <MapContainer
        center={centre}
        zoom={7}
        scrollWheelZoom={true}
        sx={{ height: "100%" }}
        ref={mapRef}
      >
        <BackgroundTiles />
        <TooltipDragFix />
        <Pane name="wind" style={{ zIndex: 310 }}>
          <WindLayer />
        </Pane>
        {sectorsOnMap && (
          <Pane name="sectors" style={{ zIndex: 320 }}>
            <SectorLayer />
          </Pane>
        )}
        {loaOnMap && (
          <Pane name="loaBorderAreas" style={{ zIndex: 330 }}>
            <VerticalBorderAgreementLayer />
          </Pane>
        )}
        {loaOnMap && (
          <Pane name="loaBorderLines" style={{ zIndex: 340 }}>
            <LateralBorderAgreementLayer />
          </Pane>
        )}
        {areasOnMap && (
          <Pane name="areas" style={{ zIndex: 350 }}>
            <AreaLayer />
          </Pane>
        )}
        {sigmetOnMap && (
          <Pane name="sigmet" style={{ zIndex: 360 }}>
            <SigmetLayer />
          </Pane>
        )}
        {airwaysOnMap && (
          <Pane name="airways" style={{ zIndex: 370 }}>
            <AirwayLayer />
          </Pane>
        )}
        <Pane name="navaids" style={{ zIndex: 380 }}>
          <NavaidLayer />
        </Pane>
        <AerodromeLayer />
      </MapContainer>
      <Flex
        sx={{ flexDirection: "column", gap: 2, overflow: "hidden", padding: 2 }}
      >
        <MapControls map={mapRef} />
      </Flex>
    </SidebarLayout>
  )
}
